import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function PositDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: posit } = useQuery({
    queryKey: ['admin-posit', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await supabase
        .from('posits')
        .select('id, title, user_id, is_public, created_at, updated_at')
        .eq('id', id)
        .maybeSingle();
      return data as any;
    },
    enabled: !!id,
  });

  const { data: sessions } = useQuery({
    queryKey: ['admin-posit-sessions', id],
    queryFn: async () => {
      if (!id) return [];
      const { data } = await supabase
        .from('chat_sessions')
        .select('id, session_type, created_at, updated_at')
        .eq('posit_id', id)
        .order('updated_at', { ascending: false })
        .limit(100);
      return (data || []) as any[];
    },
    enabled: !!id,
  });

  return (
    <DashboardLayout title="Posit" maxWidth="6xl" headerActions={<Button variant="outline" onClick={() => navigate('/admin/business/posits')}>Back</Button>}>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 border rounded-lg md:col-span-1">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Title</div>
              <div className="font-medium">{posit?.title || '—'}</div>
            </div>
            <div className="space-y-1 mt-4">
              <div className="text-sm text-muted-foreground">Owner</div>
              <div className="font-mono text-xs">{posit?.user_id || '—'}</div>
            </div>
            <div className="space-y-1 mt-4">
              <div className="text-sm text-muted-foreground">Public</div>
              <div className="font-medium">{posit?.is_public ? 'Yes' : 'No'}</div>
            </div>
            <div className="space-y-1 mt-4">
              <div className="text-sm text-muted-foreground">Created</div>
              <div className="font-medium">{posit?.created_at ? new Date(posit.created_at).toLocaleString() : '—'}</div>
            </div>
            <div className="space-y-1 mt-4">
              <div className="text-sm text-muted-foreground">Updated</div>
              <div className="font-medium">{posit?.updated_at ? new Date(posit.updated_at).toLocaleString() : '—'}</div>
            </div>
          </div>
          <div className="p-4 border rounded-lg md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Recent Sessions</h2>
            </div>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(sessions || []).map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.id}</TableCell>
                      <TableCell>{s.session_type}</TableCell>
                      <TableCell>{new Date(s.updated_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {(sessions || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground">No sessions</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
    </DashboardLayout>
  );
}


